import {
  ICartFilterBody,
  ICartSearchResponseData,
  IHDCartAddressListResponseData,
} from "@/api_framework/api_modals/Cart";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useCartLevelCouponListAPI } from "@/app/hooks/api_hooks/Cart/useCartLevelCouponListAPI";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { CiDiscount1 } from "react-icons/ci";
import { LuDog } from "react-icons/lu";
import PetSelectDropdownComponent from "../../CustomerProfile/CustomerDetails/CustomerInformation/PetDropdown";
import { useCartListAPI } from "@/app/hooks/api_hooks/Cart/useCartListAPI";
import NudgesContainer from "@/app/components/ui/Nudges/NudgesContainer";
import {
  INudgesCartMedicineProps,
  useFoodOnNudgeActionInCartAPI,
} from "@/app/hooks/api_hooks/nudges/useFoodOnNudgeActionInCartAPI";
import { useCartDetailsAPI } from "@/app/hooks/api_hooks/Cart/useCartDetailsAPI";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { ICartState } from "@/app_redux/reducers/slices/auth/cart_slice";
import UnifiedCartProductSearchComponent from "../UnifiedCartProductSearchComponent";
import { useSearchParams } from "react-router-dom";

interface ICartHeaderCartLevelCouponFormFields {
  cart_level_coupon_id: string;
}

const CartCreationHeaderComponent: React.FC<{
  hidePets?: boolean;
  activeCartId: string;
  clinicId: string;
  handleRemoveCartLevelCoupon: () => void;
  handleApplyCartLevelCoupon: (couponId: string) => void;
  cartLevelCouponId: string;
  fetchLotDetails: (item: ICartSearchResponseData, swap?: boolean, applyMembershipCoupons?: boolean) => void;
  currentAddress?: IHDCartAddressListResponseData | null;
}> = (props) => {
  const {
    hidePets = false,
    fetchLotDetails,
    activeCartId,
    clinicId,
    handleApplyCartLevelCoupon,
    handleRemoveCartLevelCoupon,
    cartLevelCouponId,
    currentAddress,
  } = props;
  const { getRouteKey } = useRouter();
  const { execute: fetchCartLevelCoupons } = useCartLevelCouponListAPI();
  const { routeState } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"));
  const { customerId, petId } = routeState.state as ICustomerProfileState;
  const { cartLevelCoupon } = useRouteInfo(getRouteKey("CART_MANAGEMENT", "id"))?.routeState?.state as ICartState;
  const { execute: fetchCartList } = useCartListAPI();
  const { execute: addFoodOnCart } = useFoodOnNudgeActionInCartAPI();
  const { execute: refetchCartDetails } = useCartDetailsAPI();
  const [hasShownNudgesForMirror, setHasShownNudgesForMirror] = useState(false);
  const mappedClinicId = useAppSelector((state) => state.root.UIStates.mappedClinicId);
  const { customerPets } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id")).routeState
    .state as ICustomerProfileState;
  const [hasShownNudges, setHasShownNudges] = useState(false);
  const cartLevelCouponOptions = useSelectMenuReducer(cartLevelCoupon, "coupon_name", "coupon_id");
  const alert_category_list = ["Membership", "Upsell / Re-Sell Food", "High Risk Cart"];
  const alert_category_listForMirror = ["Screen Mirror Disconnected"];
  const userRole = useAppSelector((state) => state?.auth?.userPermissions ?? state?.auth?.userRole);

  const formHook = useForm<ICartHeaderCartLevelCouponFormFields>({
    defaultValues: {
      cart_level_coupon_id: "",
    },
  });

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let hasChecked = false;

    const checkAndSetNudges = () => {
      if (hasChecked) {
        return;
      }
      hasChecked = true;
      const mirrorcartId = localStorage.getItem("Mirrorcartid");
      const shouldShowNudges = !mirrorcartId;
      setHasShownNudgesForMirror(shouldShowNudges);
      if (shouldShowNudges) {
        localStorage.setItem("MirrorNudgesShown", "true");
      }
    };
    timeoutId = setTimeout(checkAndSetNudges, 30000);

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "Mirrorcartid") {
        if (e.newValue && e.newValue.trim()) {
          if (!hasChecked) {
            if (timeoutId) clearTimeout(timeoutId);
            hasChecked = true;
            setHasShownNudgesForMirror(false);
            localStorage.removeItem("MirrorNudgesShown");
          } else {
            setHasShownNudgesForMirror(false);
            localStorage.removeItem("MirrorNudgesShown");
          }
        }
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    formHook.setValue("cart_level_coupon_id", cartLevelCouponId);
  }, [cartLevelCouponId]);

  useEffect(() => {
    if (activeCartId && clinicId) {
      fetchCartLevelCoupons(activeCartId, clinicId);
    }
  }, [activeCartId, clinicId]);

  const handleSearchItemClick = (item: ICartSearchResponseData | null) => {
    if (!item) return;
    fetchCartLevelCoupons(activeCartId, clinicId);
    if (item?.tag === "#membership") {
      fetchLotDetails(item, false, true);
    } else {
      fetchLotDetails(item, false);
    }
  };

  useEffect(() => {
    if (customerId) {
      const filterBody: ICartFilterBody = {
        cart_status: ["Unbilled"],
        start_date: null,
        end_date: null,
      };
      fetchCartList(filterBody, customerId);
    }
  }, []);

  const addMembershipAndFoodInCart = async (selectedItems: string[], item_tag: string): Promise<any> => {
    if (item_tag === "#product") {
      const constructedBody: INudgesCartMedicineProps = {
        customer_id: customerId,
        pet_id: petId,
        cart_id: activeCartId,
        food_items: selectedItems,
        source_type: "product",
      };
      addFoodOnCart(constructedBody).then(({ status }) => {
        if (status) {
          fetchCartLevelCoupons(activeCartId, mappedClinicId);
          refetchCartDetails(activeCartId, mappedClinicId, "normal", cartLevelCouponId);
          fetchCartLevelCoupons(activeCartId, mappedClinicId);
        }
      });
      return { status: true };
    } else if (item_tag === "#service") {
      const constructedBody: INudgesCartMedicineProps = {
        customer_id: customerId,
        pet_id: petId,
        cart_id: activeCartId,
        grooming_membership_list: selectedItems,
        source_type: "service",
        clinic_id: mappedClinicId,
      };
      addFoodOnCart(constructedBody).then(({ status }) => {
        if (status) {
          fetchCartLevelCoupons(activeCartId, mappedClinicId);
          refetchCartDetails(activeCartId, mappedClinicId, "normal", cartLevelCouponId);
          fetchCartLevelCoupons(activeCartId, mappedClinicId);
        }
      });
      return { status: true };
    } else {
      const constructedBody: INudgesCartMedicineProps = {
        customer_id: customerId,
        pet_id: petId,
        cart_id: activeCartId,
        membership_list: selectedItems,
        source_type: "membership",
        clinic_id: mappedClinicId,
      };
      addFoodOnCart(constructedBody).then(({ status }) => {
        if (status) {
          fetchCartLevelCoupons(activeCartId, mappedClinicId);
          refetchCartDetails(activeCartId, mappedClinicId, "normal", cartLevelCouponId);
          fetchCartLevelCoupons(activeCartId, mappedClinicId);
        }
      });
      return { status: true };
    }
  };

  useEffect(() => {
    if (userRole) {
      const wasShown = localStorage.getItem("nudgesShown_cart");
      const shouldShow = wasShown === null || wasShown === "false";
      if (shouldShow) {
        setHasShownNudges(true);
        localStorage.setItem("nudgesShown_cart", "true");
      }
    }
  }, [userRole]);

  // route params
  const [searchParams] = useSearchParams(); 
  const petIdFromUrl = searchParams.get("pet_id");

  return (
    <>
      <div className="border-gray-100 bg-white shadow-md px-2 py-1 w-full pb-1.5">
        <div className="flex justify-start items-center gap-x-3 w-full">
          <div className="flex justify-start items-center w-full">
            {!hidePets && (
              <div className="flex justify-start items-center">
                <div className="flex justify-center items-center bg-gray-200 p-2 mt-1 rounded-l-lg">
                  <LuDog className="w-5 h-5" />
                </div>
                <div className="flex justify-start items-center p-2 mt-1 border text-sm">
                  <PetSelectDropdownComponent
                    className="w-32"
                    variant="ONE"
                    onlyAllowActivePetSelection
                    pets={customerPets}
                  />
                </div>
              </div>
            )}
            <div className="w-full mx-2 pr-4">
              <UnifiedCartProductSearchComponent
                placeholder="Search Food & Accessories, Medicine and Others"
                containerClassName="w-full rounded-l-none"
                onSelectItem={(item) => {
                  handleSearchItemClick(item);
                }}
                inputContainerClassName="w-full"
                currentAddress={currentAddress}
              />
            </div>
          </div>
          <div className="2xl:w-72 flex justify-end items-center gap-x-2">
            <div className="flex justify-center items-center">
              <div className="border border-r-0 rounded-l-md p-2">
                <CiDiscount1 className="w-5 h-5 text-green-500" />
              </div>
              <SearchableSelectMenu
                showDropdownIcon
                disabled={!!cartLevelCouponId}
                selectItems={cartLevelCouponOptions}
                fieldError={formHook?.formState?.errors?.cart_level_coupon_id}
                errorMessages={[]}
                register={formHook.register("cart_level_coupon_id", { required: false })}
                className="w-full rounded-none"
                inputContainerClassName="rounded-l-none"
                containerClassName="w-48"
                listBoxClassName="w-[250px]"
                placeholder="Select Cart Level Coupon"
                onSelectItem={(item) => {
                  formHook.setValue("cart_level_coupon_id", item.id);
                }}
                defaultSelected={cartLevelCouponOptions?.find(
                  (item) => item.id === formHook.watch("cart_level_coupon_id"),
                )}
              />
            </div>
            <button
              onClick={() => {
                if (!cartLevelCouponId) {
                  handleApplyCartLevelCoupon(formHook.watch("cart_level_coupon_id"));
                } else {
                  handleRemoveCartLevelCoupon();
                  formHook.setValue("cart_level_coupon_id", "");
                }
              }}
              className={clsx(
                "border px-4 py-2 rounded-md text-xs",
                !formHook.watch("cart_level_coupon_id")
                  ? "bg-gray-300 text-gray-700 border-gray-300 cursor-not-allowed"
                  : "bg-blue-500 text-white border-gray-500",
              )}
              disabled={!formHook.watch("cart_level_coupon_id")}
            >
              {cartLevelCouponId ? "Remove" : "Apply"}
            </button>
          </div>
        </div>
      </div>
      {hasShownNudges && (
        <NudgesContainer
          source="Cart"
          alert_category_list={alert_category_list}
          cart_id={activeCartId}
          addMembershipAndFoodInCart={addMembershipAndFoodInCart}
          pet_Id={petIdFromUrl}
        />
      )}
      {hasShownNudgesForMirror && (
        <NudgesContainer
          source="Cart"
          alert_category_list={alert_category_listForMirror}
          cart_id={activeCartId}
          addMembershipAndFoodInCart={addMembershipAndFoodInCart}
          pet_Id={petIdFromUrl}
        />
      )}
    </>
  );
};

export default CartCreationHeaderComponent;
